import { useMemo, type CSSProperties } from "react";
import type { Item, Phase, Project } from "@/lib/types";
import { TYPE_META } from "@/lib/types";
import { parseDate, startOfDay, shortDate, isOverdue } from "@/lib/format";
import { riskOf } from "@/lib/deps";
import { card, Dot } from "./atoms";

const MS_DAY = 86_400_000;

type SubCount = { done: number; total: number };

/** Monday on or before the given date (Mon = start of week). */
function mondayOnOrBefore(ms: number): number {
  const d = startOfDay(new Date(ms));
  const dow = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  return d.getTime() - dow * MS_DAY;
}

function progressOf(item: Item, sub?: SubCount): number {
  if (sub && sub.total > 0) return Math.round((sub.done / sub.total) * 100);
  return item.status === "done" ? 100 : 0;
}

/** State drives bar color: overdue → bad, done → good, otherwise accent. */
function toneOf(item: Item): { fill: string; track: string } {
  if (isOverdue(item)) return { fill: "var(--bad)", track: "var(--badTint)" };
  if (item.status === "done") return { fill: "var(--good)", track: "var(--goodTint)" };
  return { fill: "var(--accent)", track: "var(--tint2)" };
}

const HEADER_H = 46;
const PROJECT_H = 30;
const PHASE_H = 32;
const ITEM_H = 38;
const BAR_H = 18;
const LOAD_H = 44;

export function GanttTimeline({
  phases,
  items,
  subCounts,
  onOpen,
  mobile,
  projectOf,
  weeklyTargetHours = 30,
}: {
  phases: Phase[];
  items: Item[];
  subCounts?: Map<string, SubCount>;
  onOpen: (i: Item) => void;
  mobile: boolean;
  /** Supplied by Roadmap, where phases from many projects share one grid. */
  projectOf?: (phase: Phase) => Project | undefined;
  weeklyTargetHours?: number;
}) {
  const RAIL_W = mobile ? 150 : 262;
  const WEEK_W = mobile ? 84 : 108;

  const model = useMemo(() => {
    // Range: every item start/due and phase start/end, plus today.
    let lo = Infinity, hi = -Infinity;
    const consider = (s?: string) => {
      const t = parseDate(s)?.getTime();
      if (t != null) { lo = Math.min(lo, t); hi = Math.max(hi, t); }
    };
    for (const it of items) { consider(it.start); consider(it.due); }
    for (const ph of phases) { consider(ph.start); consider(ph.end); }
    const today = startOfDay().getTime();
    if (!isFinite(lo)) { lo = today; hi = today + 30 * MS_DAY; }
    lo = Math.min(lo, today); hi = Math.max(hi, today);

    const gridStart = mondayOnOrBefore(lo);
    const weeks: { label: string; range: string; startMs: number }[] = [];
    let cursor = gridStart, n = 1;
    // Cover through the week that holds `hi`.
    while (cursor <= hi) {
      const end = cursor + 6 * MS_DAY;
      weeks.push({
        label: `Week ${n}`,
        range: `${shortDate(new Date(cursor).toISOString())} – ${shortDate(new Date(end).toISOString())}`,
        startMs: cursor,
      });
      cursor += 7 * MS_DAY; n++;
    }
    if (weeks.length === 0) weeks.push({ label: "Week 1", range: "", startMs: gridStart });
    return { gridStart, weeks, today };
  }, [items, phases]);

  const { gridStart, weeks, today } = model;
  const gridW = weeks.length * WEEK_W;
  // px offset of a date within the week grid.
  const x = (ms: number) => Math.max(0, Math.min(gridW, ((ms - gridStart) / MS_DAY / 7) * WEEK_W));
  const todayX = x(today);

  const itemsByPhase = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of items) {
      const arr = m.get(it.phase) || m.set(it.phase, []).get(it.phase)!;
      arr.push(it);
    }
    const key = (it: Item) => parseDate(it.start)?.getTime() ?? parseDate(it.due)?.getTime() ?? Infinity;
    for (const arr of m.values()) arr.sort((a, b) => key(a) - key(b) || a.position - b.position);
    return m;
  }, [items]);

  /**
   * Row layout, computed before render.
   *
   * Arrows are drawn in one SVG overlay across the whole grid, so every row's
   * vertical position has to be known up front — deriving it during render
   * would mean measuring the DOM afterwards.
   */
  const layout = useMemo(() => {
    const rows: (
      | { kind: "project"; project: Project; y: number }
      | { kind: "phase"; phase: Phase; y: number }
      | { kind: "item"; item: Item; y: number }
    )[] = [];
    const itemY = new Map<string, number>();
    let y = 0;
    let lastProjectId: string | null = null;

    for (const ph of phases) {
      const phItems = itemsByPhase.get(ph.id) || [];
      if (phItems.length === 0) continue;

      // Name the project once above its phases. Without it a row reads
      // "Draft chapters" and never says whose.
      const project = projectOf?.(ph);
      if (project && project.id !== lastProjectId) {
        rows.push({ kind: "project", project, y });
        y += PROJECT_H;
        lastProjectId = project.id;
      }

      rows.push({ kind: "phase", phase: ph, y });
      y += PHASE_H;
      for (const it of phItems) {
        rows.push({ kind: "item", item: it, y });
        itemY.set(it.id, y);
        y += ITEM_H;
      }
    }
    return { rows, itemY, height: y };
  }, [phases, itemsByPhase, projectOf]);

  const dated = items.some((i) => parseDate(i.start) || parseDate(i.due));
  if (!dated) {
    return (
      <div style={{ ...card, padding: "26px 20px", fontSize: 13.5, color: "var(--muted2)", textAlign: "center" }}>
        No dated items yet. Add a start or due date to see work on the timeline.
      </div>
    );
  }

  const railCell: CSSProperties = {
    position: "sticky", left: 0, zIndex: 4, width: RAIL_W, flex: "none",
    display: "flex", alignItems: "center", gap: 8, padding: "0 14px",
    borderRight: "1px solid var(--line)", background: "var(--surface)",
  };

  const rangeLabel = `${shortDate(new Date(gridStart).toISOString())} – ${shortDate(new Date(gridStart + (weeks.length * 7 - 1) * MS_DAY).toISOString())}`;

  return (
    <div style={{ ...card, overflow: "hidden" }}>
      {/* Legend + overall range */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderBottom: "1px solid var(--line)", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: "var(--fw-title)" }}>{rangeLabel}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <LegendSwatch color="var(--accent)" label="In progress" />
          <LegendSwatch color="var(--good)" label="Completed" />
          <LegendSwatch color="var(--bad)" label="Overdue" />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 9, height: 9, background: "var(--purple)", transform: "rotate(45deg)", display: "inline-block" }} />
            <span style={{ fontSize: 11.5, color: "var(--ink3)" }}>Milestone</span>
          </span>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <div style={{ position: "relative", width: RAIL_W + gridW, minWidth: "100%" }}>
          {/* Week gridlines (behind rows) */}
          {weeks.map((w, i) => (
            <div key={i} style={{ position: "absolute", top: HEADER_H, bottom: 0, left: RAIL_W + x(w.startMs), width: 1, background: "var(--tint2)", zIndex: 0 }} />
          ))}
          {/* Today line (above bars) */}
          {today >= gridStart && (
            <div style={{ position: "absolute", top: HEADER_H, bottom: 0, left: RAIL_W + todayX, width: 2, background: "var(--bad)", zIndex: 3 }} />
          )}

          {/* Header row */}
          <div style={{ display: "flex", height: HEADER_H, borderBottom: "1px solid var(--line)" }}>
            <div style={{ ...railCell, zIndex: 5 }}>
              <span style={{ fontSize: 11, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--muted)" }}>Task</span>
            </div>
            <div style={{ position: "relative", width: gridW, flex: "none" }}>
              {weeks.map((w, i) => (
                <div key={i} style={{ position: "absolute", left: i * WEEK_W, top: 0, width: WEEK_W, height: "100%", padding: "6px 10px", borderLeft: i === 0 ? "none" : "1px solid var(--tint2)" }}>
                  <div style={{ fontSize: 12.5, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-tight)" }}>{w.label}</div>
                  <div className="mono" style={{ fontSize: 9.5, color: "var(--muted2)" }}>{w.range}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Load row: what each week is carrying, above the work itself. */}
          <LoadRow
            items={items} weeks={weeks} railCell={railCell} gridW={gridW}
            weekW={WEEK_W} target={weeklyTargetHours}
          />

          {/* Rows, driven by the precomputed layout so arrows and rows agree. */}
          <div style={{ position: "relative" }}>
            <DependencyArrows
              layout={layout} items={items} x={x} gridW={gridW} railW={RAIL_W} mobile={mobile}
            />
            {layout.rows.map((row) => {
              if (row.kind === "project") {
                return (
                  <div key={`p:${row.project.id}:${row.y}`} style={{ display: "flex", height: PROJECT_H, background: "var(--tint)", position: "relative", zIndex: 1, borderTop: "1px solid var(--line)" }}>
                    <div style={{ ...railCell, background: "var(--tint)" }}>
                      <Dot color={row.project.color} size={9} />
                      <span style={{ fontSize: 12.5, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-tight)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.project.name}</span>
                    </div>
                    <div style={{ width: gridW, flex: "none" }} />
                  </div>
                );
              }

              if (row.kind === "phase") {
                const ph = row.phase;
                const phItems = itemsByPhase.get(ph.id) || [];
                let ps = parseDate(ph.start)?.getTime() ?? Infinity;
                let pe = parseDate(ph.end)?.getTime() ?? -Infinity;
                for (const it of phItems) {
                  const st = parseDate(it.start)?.getTime() ?? parseDate(it.due)?.getTime();
                  const dt = parseDate(it.due)?.getTime() ?? parseDate(it.start)?.getTime();
                  if (st != null) ps = Math.min(ps, st);
                  if (dt != null) pe = Math.max(pe, dt);
                }
                const hasSpan = isFinite(ps) && isFinite(pe);
                const sumLeft = hasSpan ? x(Math.min(ps, pe)) : 0;
                const sumW = hasSpan ? Math.max(6, x(Math.max(ps, pe) + MS_DAY) - sumLeft) : 0;
                // The PROJECT's colour, not the phase's own, so a project reads
                // as one block down the grid.
                const barColor = projectOf?.(ph)?.color ?? ph.color;
                return (
                  <div key={ph.id} style={{ display: "flex", height: PHASE_H, background: "var(--bg2)", position: "relative", zIndex: 1 }}>
                    <div style={{ ...railCell, background: "var(--bg2)" }}>
                      <Dot color={barColor} size={8} />
                      <span style={{ fontSize: 13, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-tight)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ph.name}</span>
                      <span className="mono" style={{ marginLeft: "auto", fontSize: 10, color: "var(--muted2)", flex: "none" }}>{shortDate(ph.start)}{ph.start && ph.end ? " – " : ""}{shortDate(ph.end)}</span>
                    </div>
                    <div style={{ position: "relative", width: gridW, flex: "none" }}>
                      {hasSpan && (
                        <div style={{ position: "absolute", left: sumLeft, top: PHASE_H / 2 - 3, width: sumW, height: 6, borderRadius: 999, background: barColor, opacity: 0.22 }} />
                      )}
                    </div>
                  </div>
                );
              }

              const it = row.item;
              const sMs = parseDate(it.start)?.getTime() ?? parseDate(it.due)?.getTime()!;
              const dMs = parseDate(it.due)?.getTime() ?? sMs;
              const isMs = it.type === "milestone";
              const tone = toneOf(it);
              const prog = progressOf(it, subCounts?.get(it.id));
              const barLeft = x(Math.min(sMs, dMs));
              const barW = Math.max(mobile ? 30 : 34, x(Math.max(sMs, dMs) + MS_DAY) - barLeft);
              const done = it.status === "done";
              return (
                <div key={it.id} onClick={() => onOpen(it)} className="row-hover" style={{ display: "flex", height: ITEM_H, cursor: "pointer", borderBottom: "1px solid var(--tint2)", position: "relative", zIndex: 1 }}>
                  <div style={{ ...railCell, paddingLeft: 26 }}>
                    <span style={{ color: TYPE_META[it.type].color, fontSize: 12, flex: "none" }}>{TYPE_META[it.type].glyph}</span>
                    <span style={{ fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: done ? "var(--muted)" : "var(--ink2)", textDecoration: done ? "line-through" : "none" }}>{it.title}</span>
                  </div>
                  <div style={{ position: "relative", width: gridW, flex: "none" }}>
                    {isMs ? (
                      <span title={shortDate(it.due)} style={{ position: "absolute", left: x(dMs) - 6, top: ITEM_H / 2 - 6, width: 12, height: 12, background: "var(--purple)", transform: "rotate(45deg)", borderRadius: 2 }} />
                    ) : (
                      <div style={{ position: "absolute", left: barLeft, top: ITEM_H / 2 - BAR_H / 2, width: barW, height: BAR_H, borderRadius: 999, background: tone.track, overflow: "hidden" }} title={`${shortDate(it.start)} – ${shortDate(it.due)}`}>
                        <div style={{ position: "absolute", inset: 0, width: `${prog}%`, background: tone.fill, borderRadius: 999 }} />
                        <span style={{ position: "absolute", left: 8, top: 0, height: "100%", display: "flex", alignItems: "center", fontSize: 10.5, fontWeight: "var(--fw-medium)", color: prog >= 45 ? "#fff" : "var(--ink3)" }}>{prog}%</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 14, height: 8, borderRadius: 999, background: color, display: "inline-block" }} />
      <span style={{ fontSize: 11.5, color: "var(--ink3)" }}>{label}</span>
    </span>
  );
}


/**
 * Forward load per week.
 *
 * Counting items due told you how many things land, not how much work that is —
 * five one-hour reviews and five chapters read identically. With
 * `Item.estimateHours` it becomes hours against a weekly target.
 *
 * Where a week carries no estimates at all it falls back to the item count and
 * says so: a partial sum is worse than a count, because it reads as complete
 * when it is not.
 */
function LoadRow({ items, weeks, railCell, gridW, weekW, target }: {
  items: Item[];
  weeks: { startMs: number }[];
  railCell: CSSProperties;
  gridW: number;
  weekW: number;
  target: number;
}) {
  const buckets = weeks.map((w) => {
    const end = w.startMs + 7 * MS_DAY;
    const due = items.filter((i) => {
      if (i.status === "done") return false;
      const d = parseDate(i.due)?.getTime() ?? parseDate(i.start)?.getTime();
      return d != null && d >= w.startMs && d < end;
    });
    const withEstimate = due.filter((i) => typeof i.estimateHours === "number" && i.estimateHours > 0);
    const hours = withEstimate.reduce((n, i) => n + (i.estimateHours ?? 0), 0);
    return { count: due.length, hours, estimated: withEstimate.length > 0 };
  });

  const ceiling = Math.max(target, ...buckets.map((b) => b.hours)) * 1.15 || 1;

  return (
    <div style={{ display: "flex", height: LOAD_H, borderBottom: "1px solid var(--line)", position: "relative", zIndex: 1, background: "var(--surface)" }}>
      <div style={{ ...railCell }}>
        <span style={{ fontSize: 11, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--muted)" }}>Load</span>
        <span className="mono" style={{ marginLeft: "auto", fontSize: 10, color: "var(--muted2)" }}>{target} h/wk</span>
      </div>
      <div style={{ position: "relative", width: gridW, flex: "none" }}>
        {buckets.map((b, i) => {
          const over = b.estimated && b.hours > target;
          const h = b.estimated ? Math.min(1, b.hours / ceiling) * (LOAD_H - 14) : 0;
          return (
            <div
              key={i}
              title={b.estimated ? `${b.hours} h estimated across ${b.count} item${b.count === 1 ? "" : "s"}` : `${b.count} item${b.count === 1 ? "" : "s"} due — no estimates`}
              style={{ position: "absolute", left: i * weekW, width: weekW, bottom: 0, height: LOAD_H, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 2, padding: "0 12px 5px" }}
            >
              {b.estimated ? (
                <>
                  <span className="mono" style={{ fontSize: 10, color: over ? "var(--warn)" : "var(--muted2)" }}>{b.hours}h</span>
                  <span style={{ width: "100%", height: Math.max(2, h), background: over ? "var(--warn)" : "var(--accentSoft)", borderRadius: 2 }} />
                </>
              ) : (
                <span style={{ fontSize: 10, color: "var(--muted3)" }}>{b.count || "—"}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * `Item.dependsOn` as finish-to-start elbows.
 *
 * Tone comes from `riskOf` — the same predicate the Work registers and the task
 * drawer banner use, so the three cannot disagree about what is at risk.
 *
 * When the dependent starts before its prerequisite finishes, the elbow routes
 * backwards and arrives on top of the bar rather than beside it. That shape is
 * the finding: the plan says do B before A is done.
 */
function DependencyArrows({ layout, items, x, gridW, railW, mobile }: {
  layout: { itemY: Map<string, number>; height: number };
  items: Item[];
  x: (ms: number) => number;
  gridW: number;
  railW: number;
  mobile: boolean;
}) {
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  if (mobile) return null; // no room, and nothing to hover for the tooltip

  const endOf = (i: Item) => parseDate(i.due)?.getTime() ?? parseDate(i.start)?.getTime();
  const startOf = (i: Item) => parseDate(i.start)?.getTime() ?? parseDate(i.due)?.getTime();

  const links = items.flatMap((it) => {
    if (!it.dependsOn) return [];
    const from = byId.get(it.dependsOn);
    if (!from) return [];
    const fy = layout.itemY.get(from.id);
    const ty = layout.itemY.get(it.id);
    const fEnd = endOf(from);
    const tStart = startOf(it);
    if (fy == null || ty == null || fEnd == null || tStart == null) return [];

    const risk = riskOf(it, byId);
    return [{
      key: `${from.id}->${it.id}`,
      x1: x(fEnd + MS_DAY), y1: fy + ITEM_H / 2,
      x2: x(tStart), y2: ty + ITEM_H / 2,
      tone: risk ? "var(--warn)" : "var(--line2)",
      backwards: tStart < fEnd,
      title: risk ? risk.message : `Waits for “${from.title}”`,
    }];
  });

  if (links.length === 0) return null;

  return (
    <svg
      width={railW + gridW} height={layout.height}
      style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", zIndex: 2, overflow: "visible" }}
    >
      {links.map((l) => {
        const x1 = railW + l.x1;
        const x2 = railW + l.x2;
        // Forward: out, down, in. Backwards: out, around, and back on top.
        const midX = l.backwards ? x1 + 12 : Math.max(x1 + 10, x2 - 10);
        const d = l.backwards
          ? `M ${x1} ${l.y1} H ${midX} V ${l.y2 - ITEM_H / 2 + 3} H ${x2} V ${l.y2}`
          : `M ${x1} ${l.y1} H ${midX} V ${l.y2} H ${x2}`;
        return (
          <g key={l.key}>
            <title>{l.title}</title>
            <path d={d} fill="none" stroke={l.tone} strokeWidth={1.5} strokeDasharray={l.backwards ? "3 2" : undefined} />
            <circle cx={x2} cy={l.y2} r={2.5} fill={l.tone} />
          </g>
        );
      })}
    </svg>
  );
}
